import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import './Pages.css';

const Register = () => {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        yearName: '',
        customYearName: '',
        dateJoined: '',
        customDateJoined: '',
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [ministryYears, setMinistryYears] = useState([]);
    const [usingCustomYear, setUsingCustomYear] = useState(false);
    const [loadingYears, setLoadingYears] = useState(true);

    const { register } = useAuth();
    const { showToast } = useToast();
    const navigate = useNavigate();

    useEffect(() => {
        loadMinistryYears();
    }, []);

    const loadMinistryYears = async () => {
        try {
            const yearsQuery = query(collection(db, 'ministryYears'), orderBy('startDate', 'desc'));
            const snapshot = await getDocs(yearsQuery);
            const years = [];
            snapshot.forEach(doc => {
                years.push({ id: doc.id, ...doc.data() });
            });
            setMinistryYears(years);
        } catch (error) {
            console.error('Error loading ministry years:', error);
            showToast('Error loading year groups', 'error');
        } finally {
            setLoadingYears(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.id]: e.target.value
        });
    };

    const handleYearSelect = (e) => {
        const selectedYear = e.target.value;
        setFormData({
            ...formData,
            yearName: selectedYear,
            dateJoined: selectedYear ? ministryYears.find(y => y.yearName === selectedYear)?.startDate?.toDate().toISOString().split('T')[0] || '' : ''
        });
    };

    const validateForm = () => {
        if (!formData.fullName.trim()) {
            setError('Full name is required');
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Please enter a valid email address');
            return false;
        }

        const phoneRegex = /^254[0-9]{9}$/;
        if (!phoneRegex.test(formData.phone)) {
            setError('Please enter a valid phone number in format 254XXXXXXXXX');
            return false;
        }

        if (usingCustomYear) {
            if (!formData.customYearName.trim()) {
                setError('Year group name is required');
                return false;
            }
            if (!formData.customDateJoined) {
                setError('Date joined is required');
                return false;
            }
        } else {
            if (!formData.yearName) {
                setError('Please select a year group');
                return false;
            }
        }

        if (formData.password.length < 8) {
            setError('Password must be at least 8 characters');
            return false;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const userDetails = {
                fullName: formData.fullName,
                phone: formData.phone,
                yearName: usingCustomYear ? formData.customYearName : formData.yearName,
                dateJoined: usingCustomYear ? new Date(formData.customDateJoined) : new Date(formData.dateJoined)
            };

            await register(formData.email, formData.password, userDetails);
            showToast('Registration successful! Please check your email for verification.', 'success');
            navigate('/login?registered=true');
        } catch (error) {
            console.error('Registration error:', error);
            let errorMessage = 'Registration failed. Please try again.';
            
            switch (error.code) {
                case 'auth/email-already-in-use':
                    errorMessage = 'This email is already registered.';
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Invalid email address.';
                    break;
                case 'auth/weak-password':
                    errorMessage = 'Password is too weak.';
                    break;
                case 'auth/network-request-failed':
                    errorMessage = 'Network error. Please check your connection.';
                    break;
                default:
                    errorMessage = error.message;
            }
            
            setError(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    const formatPhoneNumber = (value) => {
        let phone = value.replace(/\D/g, '');
        if (phone.length > 0 && !phone.startsWith('254')) {
            if (phone.startsWith('0')) {
                phone = '254' + phone.substring(1);
            } else if (phone.startsWith('7')) {
                phone = '254' + phone;
            }
        }
        return phone;
    };

    return (
        <div className="auth-page">
            <div className="auth-container" style={{ maxWidth: '600px' }}>
                <div className="auth-header">
                    <h1 className="auth-title">Join Our Ministry</h1>
                    <p className="auth-subtitle">Create your account to become part of our community</p>
                </div>

                {error && <div className="auth-error-message">{error}</div>}

                {loadingYears ? (
                    <div className="text-center">
                        <i className="fas fa-spinner fa-spin fa-2x"></i>
                        <p>Loading year groups...</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="auth-form-group">
                            <label htmlFor="fullName">Full Name *</label>
                            <input
                                type="text"
                                id="fullName"
                                className="auth-form-control"
                                placeholder="Enter your full name"
                                value={formData.fullName}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="auth-form-group">
                            <label htmlFor="email">Email Address *</label>
                            <input
                                type="email"
                                id="email"
                                className="auth-form-control"
                                placeholder="Enter your email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="auth-form-group">
                            <label htmlFor="phone">Phone Number *</label>
                            <input
                                type="tel"
                                id="phone"
                                className="auth-form-control"
                                placeholder="254XXXXXXXXX"
                                value={formData.phone}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    phone: formatPhoneNumber(e.target.value)
                                })}
                                required
                            />
                            <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                                Format: 254XXXXXXXXX
                            </small>
                        </div>

                        <div className="auth-form-group">
                            <label htmlFor="yearName">Year Name *</label>
                            <select
                                id="yearName"
                                className="auth-form-control"
                                value={formData.yearName}
                                onChange={handleYearSelect}
                                disabled={usingCustomYear}
                                required={!usingCustomYear}
                            >
                                <option value="">Select your year name</option>
                                {ministryYears.map(year => (
                                    <option key={year.id} value={year.yearName}>
                                        {year.yearName}
                                    </option>
                                ))}
                            </select>

                            <div style={{ marginTop: '10px' }}>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={usingCustomYear}
                                        onChange={(e) => setUsingCustomYear(e.target.checked)}
                                    />
                                    <span style={{ marginLeft: '8px' }}>My year group is not listed</span>
                                </label>
                            </div>
                        </div>

                        {usingCustomYear && (
                            <>
                                <div className="auth-form-group">
                                    <label htmlFor="customYearName">Enter Your Year Group Name *</label>
                                    <input
                                        type="text"
                                        id="customYearName"
                                        className="auth-form-control"
                                        placeholder="e.g., PHILADEPHIAN"
                                        value={formData.customYearName}
                                        onChange={handleChange}
                                        required={usingCustomYear}
                                    />
                                </div>

                                <div className="auth-form-group">
                                    <label htmlFor="customDateJoined">Date You Joined *</label>
                                    <input
                                        type="date"
                                        id="customDateJoined"
                                        className="auth-form-control"
                                        value={formData.customDateJoined}
                                        onChange={handleChange}
                                        required={usingCustomYear}
                                    />
                                </div>
                            </>
                        )}

                        {!usingCustomYear && (
                            <div className="auth-form-group">
                                <label htmlFor="dateJoined">Date Joined *</label>
                                <input
                                    type="date"
                                    id="dateJoined"
                                    className="auth-form-control"
                                    value={formData.dateJoined}
                                    readOnly
                                />
                                <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                                    This date is automatically set based on the selected year group
                                </small>
                            </div>
                        )}

                        <div className="auth-form-group">
                            <label htmlFor="password">Password (min. 8 characters) *</label>
                            <div className="auth-password-container">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    className="auth-form-control"
                                    placeholder="Create a password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    minLength="8"
                                    required
                                />
                                <i
                                    className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} auth-toggle-password`}
                                    onClick={() => setShowPassword(!showPassword)}
                                ></i>
                            </div>
                        </div>

                        <div className="auth-form-group">
                            <label htmlFor="confirmPassword">Confirm Password *</label>
                            <div className="auth-password-container">
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    id="confirmPassword"
                                    className="auth-form-control"
                                    placeholder="Confirm your password"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    minLength="8"
                                    required
                                />
                                <i
                                    className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} auth-toggle-password`}
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                ></i>
                            </div>
                        </div>

                        <button type="submit" className="auth-btn" disabled={loading}>
                            {loading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i> Creating Account...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-user-plus"></i> Create Account
                                </>
                            )}
                        </button>
                    </form>
                )}

                <div className="auth-footer">
                    <Link to="/login">Already have an account? Sign In</Link>
                </div>
            </div>
        </div>
    );
};

export default Register;
